import { Divider, Grid } from '@mui/material'
import Button from '@mui/material/Button'
import { Form, Formik } from 'formik'
import React, { useMemo } from 'react'
import * as Yup from 'yup'
import FormikTextField from '../../Formik/FormikTextField'
import { useTranslation } from 'react-i18next'

export interface IPasswordResetFormData {
  email: string
}

const PasswordResetFormFields: React.FC = () => {
  const { t } = useTranslation()
  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <FormikTextField name='email' label={t('formLabels.email', 'Email')} type='email' required />
      </Grid>
    </Grid>
  )
}

interface IProps {
  formErrors: { messages: string[]; field?: string }[] | null

  onSubmit(values: IPasswordResetFormData): Promise<void>
}

interface IDialogProps extends IProps {
  variant: 'dialog'

  handleCloseModal(): void

  handleSignIn(): void
}

interface IDefaultProps extends IProps {
  variant?: 'default'
}

const PasswordResetForm: React.FC<IDialogProps | IDefaultProps> = ({ onSubmit, formErrors, ...props }) => {
  const { t, i18n } = useTranslation()
  const schema = useMemo(
    () =>
      Yup.object().shape<IPasswordResetFormData>({
        email: Yup.string()
          .email(t('formErrors.invalidEmail', 'Invalid email'))
          .required(t('formErrors.valueIsRequired', 'This value is required')),
      }),
    [t, i18n.language]
  )
  return (
    <Formik<IPasswordResetFormData>
      initialValues={{
        email: '',
      }}
      validationSchema={schema}
      onSubmit={async values => {
        await onSubmit(values)
      }}
    >
      <>
        {props.variant === 'dialog' && (
          <>
            <Form>
              <PasswordResetFormFields />
              <div className='mt-10'>
                <Button type='submit' variant='contained' color='secondary' fullWidth>
                  <span className='font-semibold tracking-widest'>{t('retrievePassword', 'Retrieve password')}</span>
                </Button>
              </div>
            </Form>
            <div className='my-20'>
              <Divider />
            </div>
            <div className='flex items-center justify-center'>
              <span className='font-semibold'>{t('alreadyHaveAnAccount', 'Already have an account?')}</span>
              <span>
                <Button variant='text' color='secondary' onClick={props.handleSignIn} disableRipple>
                  <span className='font-semibold text-capitalize'>{t('signIn', 'Sign in')}</span>
                </Button>
              </span>
            </div>
          </>
        )}
        {(!props.variant || props.variant === 'default') && (
          <Form>
            <legend className='legend text-base'>
              {t('yourEmailAddress', 'Your E-Mail Address')}{' '}
              <span className='legend-label'>{t('required', 'Required')}</span>
            </legend>
            <PasswordResetFormFields />
            <div className='mt-10'>
              <Button type='submit' variant='contained' color='secondary' size='large' fullWidth>
                <span className='font-semibold tracking-widest'>{t('retrievePassword', 'Retrieve password')}</span>
              </Button>
            </div>
          </Form>
        )}
      </>
    </Formik>
  )
}

export default PasswordResetForm
